import { AppStrings } from "./constants/AppStrings";
import { snakeCaseToTitleLabel } from "./UserUtils";

export type MoneyTypeOption = {
  value: string;
  label: string;
};

/**
 * AppStrings is reassigned at runtime when strings.json arrives from the BE
 * (see `setAppString`). Anything that reads `AppStrings.X` at module top-level
 * gets frozen with the bootstrap values. So every helper here dereferences
 * AppStrings lazily — on each call — and components must invoke them inside
 * render (not cache the result at module scope) for the labels to refresh.
 */

/** Resolve a raw money_type key to its TCM label. */
export const moneyTypeLabel = (value: string | null | undefined): string => {
  if (!value) return "";
  const fallback = snakeCaseToTitleLabel(value);
  switch (value) {
    case "p_cash":
      return AppStrings.PIKKEM_CASH || fallback;
    case "prediction_cash":
      return AppStrings.PREDICTION_CASH || fallback;
    case "redeemable_prediction_cash":
      return AppStrings.PREDICTION_REDEEMABLE || fallback;
    case "casino_cash":
      return AppStrings.CASINO_CASH || fallback;
    case "rebet_cash":
      return AppStrings.PRIMARY_CURRENCY || fallback;
    case "rebet_coin":
      return AppStrings.SECONDARY_CURRENCY || fallback;
    default:
      return fallback;
  }
};

/**
 * Relabel a list of `{ value, label }` options (e.g. from the BE
 * filter-dropdowns endpoint). Preserves the original `value` so the filter
 * param sent to the BE doesn't change. Re-reads AppStrings per call.
 */
export const relabelMoneyTypeOptions = <T extends { value: string }>(
  options: T[],
): Array<T & { label: string }> =>
  options.map((o) => ({ ...o, label: moneyTypeLabel(o.value) }));
